import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTransportStops } from '../../services/api';
import { useMapStore } from '../../stores/mapStore';
import { pickGhanaProximity } from '../../utils/regionAvailability';
import {
  haversineMetres,
  NEARBY_TRANSPORT_RADIUS_M,
  sortStopsByDistance,
  type TransportStopFeature,
} from './transportStopUtils';

const CACHE_TTL_MS = 60_000;
const MIN_MOVE_M = 80;

type StopsCacheEntry = { ts: number; data: TransportStopFeature[] };

const stopsCache = new Map<string, StopsCacheEntry>();
const inflight = new Map<string, Promise<TransportStopFeature[]>>();

function cacheKey(lat: number, lng: number, radius: number): string {
  return `${lat.toFixed(4)}:${lng.toFixed(4)}:${radius}`;
}

function parseStopFeatures(stopsFc: GeoJSON.FeatureCollection): TransportStopFeature[] {
  return (stopsFc.features ?? []).filter(
    (f): f is TransportStopFeature => f.geometry?.type === 'Point',
  );
}

async function fetchNearbyStops(
  lat: number,
  lng: number,
  radius: number,
  force = false,
): Promise<TransportStopFeature[]> {
  const key = cacheKey(lat, lng, radius);
  const cached = stopsCache.get(key);
  if (!force && cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.data;
  }

  const pending = inflight.get(key);
  if (pending) return pending;

  const request = getTransportStops(lat, lng, radius)
    .then((stopsFc) => {
      const features = parseStopFeatures(stopsFc);
      stopsCache.set(key, { ts: Date.now(), data: features });
      return features;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, request);
  return request;
}

export function useNearbyTransportStops(radius = NEARBY_TRANSPORT_RADIUS_M, enabled = true) {
  const { userLocation, center } = useMapStore();
  const proximity = pickGhanaProximity(userLocation, center);
  const lat = proximity?.latitude ?? null;
  const lng = proximity?.longitude ?? null;

  const anchor = useMemo(
    () => (lat != null && lng != null ? { lat, lng } : null),
    [lat, lng],
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stops, setStops] = useState<TransportStopFeature[]>([]);
  const lastFetchRef = useRef<{ lat: number; lng: number; radius: number } | null>(null);
  const requestIdRef = useRef(0);

  const loadNearby = useCallback(
    async (force = false) => {
      if (lat == null || lng == null) return;

      const last = lastFetchRef.current;
      if (
        !force &&
        last &&
        last.radius === radius &&
        haversineMetres(last.lat, last.lng, lat, lng) < MIN_MOVE_M
      ) {
        return;
      }

      const key = cacheKey(lat, lng, radius);
      const cached = stopsCache.get(key);
      if (!force && cached && Date.now() - cached.ts < CACHE_TTL_MS) {
        lastFetchRef.current = { lat, lng, radius };
        setStops(cached.data);
        setError(null);
        setLoading(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const features = await fetchNearbyStops(lat, lng, radius, force);
        if (requestId !== requestIdRef.current) return;
        lastFetchRef.current = { lat, lng, radius };
        setStops(features);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Could not load transport');
        setStops([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [lat, lng, radius],
  );

  useEffect(() => {
    if (!enabled || lat == null || lng == null) return;
    void loadNearby();
  }, [enabled, lat, lng, radius, loadNearby]);

  useEffect(() => {
    if (enabled) return;
    requestIdRef.current += 1;
    setLoading(false);
  }, [enabled]);

  const sortedStops = useMemo(() => sortStopsByDistance(stops, anchor), [stops, anchor]);

  return {
    anchor,
    loading,
    error,
    sortedStops,
    reload: () => loadNearby(true),
  };
}
